import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useAdvanceSearch } from './useAdvanceSearch';
import Dropdown from '../../../../lib/inputs/Dropdown';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import Input from '../../../../components/common/form/new/Input';
import { handleChange } from '../constants/ticketlist.utils';
import { DATE_FORMATS } from '../../../../utils/dates/dateMethods';
import GIButton from '../../../../components/common/button/GIButton';
import { IAdvanceSearchProps } from '../constants/ticketlist.interface';
import Multiselect from '../../../../components/common/form/multiSelect/MultiSelect';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import AutocompleteSearch from '../../../../components/common/autocomplete-search/AutocompleteSearch';
import {
    FlexItem,
    StyledForm,
    FlexContainer,
    FlexRow,
    StyledError,
    StyledControlButton,
} from '../constants/ticketlist.styled';
import {
    AUTOCOMPLETE_DEBOUNCE_DELAY,
    AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
    CustomFiedType,
    customFieldTypes,
    defaultOptions,
    ticketPriorityOptions,
    ticketStatusAdvanceOptions,
} from '../constants/ticketlist.constants';

export const AdvanceSearch = (props: IAdvanceSearchProps) => {
    const { pageSortInfo } = props;
    const {
        handleSubmit,
        setValue,
        onSubmit,
        fetchTicketSubject,
        workgroupOptions,
        fetchAssignee,
        fetchTicketRequestors,
        roleList,
        tagsList,
        customFields,
        tkmWorkflows,
        fetchCreatedFor,
        onCancel,
        handleTicketCategoryChange,
        ticketCategory,
        customFieldOptions,
        getCustomField,
        handleCustomFieldChange,
        formState: { errors },
        isCustomTextInput,
        isCustomDateInput,
        isCustomOptionInput,
        tagListOptions,
        values,
        resetTrigger,
        setSelectedItem,
        highlightedFields,
        handleWorkgroupChange,
    } = useAdvanceSearch({ pageSortInfo });

    return (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <FlexContainer>
                <FlexRow>
                    <FlexItem highlight={highlightedFields?.ticketSubject}>
                        <AutocompleteSearch
                            {...{
                                id: 'search-summary-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'ticketSubject'),
                                selectedItems: [{ id: values.ticketSubject, label: values.ticketSubject }],
                                isSingleSelect: true,
                                label: 'ticketList.label.searchSummary',
                                isClearInputValue: false,
                                fetchResults: fetchTicketSubject,
                                loadDataOnInit: true,
                                valueKey: 'label',
                                resetTrigger,
                                initialSearchInputValue: values.ticketSubject,
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketComments}>
                        <Input
                            label={{
                                labelText: 'ticketList.label.comments',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ticketComments',
                                isInputRequired: false,
                                type: 'text',
                                value: values.ticketComments,
                            }}
                            actions={{
                                handleFieldChange: value => handleChange('ticketComments', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketQueues}>
                        <Multiselect
                            id="workgroup"
                            label="ticketList.label.workgroup"
                            value={values.ticketQueues}
                            options={workgroupOptions}
                            onChange={value => handleChange('ticketQueues', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem>
                        <Multiselect
                            id="priority"
                            label="ticketList.label.priority"
                            value={values.ticketPriority}
                            options={ticketPriorityOptions}
                            onChange={value => handleChange('ticketPriority', value, setValue)}
                        />
                    </FlexItem>
                </FlexRow>

                <FlexRow>
                    <FlexItem highlight={highlightedFields?.ticketStatus}>
                        <Multiselect
                            id="ticketStatus"
                            label="ticketList.label.ticketStatus"
                            value={values.ticketStatus}
                            options={ticketStatusAdvanceOptions}
                            onChange={value => handleChange('ticketStatus', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketNumber}>
                        <Input
                            label={{
                                labelText: 'ticketList.label.ticketNumber',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ticketNumber',
                                isInputRequired: false,
                                type: 'text',
                                value: values.ticketNumber,
                            }}
                            actions={{
                                handleFieldChange: value => handleChange('ticketNumber', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.submittedAfter}>
                        <DatePicker
                            id="submittedOnAfter"
                            name="submittedOnAfter"
                            labelText="ticketList.label.submittedOnAfter"
                            wrapperClasses={['date-picker']}
                            type="text"
                            pattern={DATE_FORMATS.MM_DD_YYYY}
                            value={values.submittedAfter}
                            withMask
                            onDateChange={value => handleChange('submittedAfter', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.submittedBefore}>
                        <DatePicker
                            id="submittedOnBefore"
                            name="submittedOnBefore"
                            labelText="ticketList.label.submittedOnBefore"
                            wrapperClasses={['date-picker']}
                            type="text"
                            pattern={DATE_FORMATS.MM_DD_YYYY}
                            value={values.submittedBefore}
                            withMask
                            onDateChange={value => handleChange('submittedBefore', value, setValue)}
                        />
                        <StyledError>{errors?.submittedBefore?.message}</StyledError>
                    </FlexItem>
                </FlexRow>
                <FlexRow>
                    <FlexItem highlight={highlightedFields?.assignee}>
                        <AutocompleteSearch
                            {...{
                                id: 'assignee-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'assignee', 'assigneeId'),
                                selectedItems: [{ id: values.assigneeId, label: values.assignee }],
                                isSingleSelect: true,
                                label: 'ticketList.label.assignee',
                                isClearInputValue: false,
                                fetchResults: fetchAssignee,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.assignee,
                            }}
                        />
                    </FlexItem>
                    <ConditionalRendering isTrue={!!tagsList?.length}>
                        <FlexItem highlight={highlightedFields?.assigneeByTag}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id="ticketList.label.assigneeByTag" />,
                                    labelClass: [''],
                                }}
                                inputData={{
                                    id: 'assigneeByTag',
                                    inputClasses: [''],
                                    options: [...defaultOptions, ...tagListOptions],
                                    type: 'dropdown',
                                    value: values.assigneeByTag,
                                    isInputRequired: false,
                                }}
                                inputActions={{
                                    handleFieldChange: value => handleChange('assigneeByTag', value, setValue),
                                }}
                            />
                        </FlexItem>
                    </ConditionalRendering>
                    <FlexItem highlight={highlightedFields?.requestedByName}>
                        <AutocompleteSearch
                            {...{
                                id: 'requested-by-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'requestedByName', 'requestedBy'),
                                selectedItems: [{ id: values.requestedBy, label: values.requestedByName }],
                                isSingleSelect: true,
                                label: 'ticketList.label.requestedBy',
                                isClearInputValue: false,
                                fetchResults: fetchTicketRequestors,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.requestedByName,
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.requestedBytagId}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.requestedByTag" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'requestedByTag',
                                inputClasses: [''],
                                options: [...defaultOptions, ...tagListOptions],
                                type: 'dropdown',
                                value: values.requestedBytagId,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleChange('requestedBytagId', value, setValue),
                            }}
                        />
                    </FlexItem>
                </FlexRow>
                <FlexRow>
                    <FlexItem highlight={highlightedFields?.createdForRole}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.createdForRole" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'createdForRole',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(roleList?.map(role => ({ value: role.id, text: role.label, ...role })) || []),
                                ],
                                type: 'dropdown',
                                value: values.createdForRole,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => {
                                    if (!value) {
                                        setValue('requester', '');
                                    }
                                    handleChange('createdForRole', value, setValue);
                                },
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.requester}>
                        <AutocompleteSearch
                            {...{
                                id: 'created-for-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'requester', 'roleId'),
                                selectedItems: [{ id: values.roleId, label: values.requester }],
                                isSingleSelect: true,
                                label: 'ticketList.label.createdFor',
                                isClearInputValue: false,
                                fetchResults: fetchCreatedFor,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.requester,
                                disabled: !values.createdForRole,
                            }}
                        />
                        <StyledError>{errors?.requester?.message}</StyledError>
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.customFieldSelect}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.customField" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'customField',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(customFields?.map(customField => ({
                                        value: customField.cfieldParamName,
                                        text: customField.cfieldName,
                                        ...customField,
                                    })) || []),
                                ],
                                type: 'dropdown',
                                value: values.customFieldSelect,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleCustomFieldChange(value),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.customFieldSelectVal}>
                        <ConditionalRendering isTrue={isCustomTextInput}>
                            <Input
                                label={{
                                    labelText: 'ticketList.label.customFieldValue',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'customFieldValue',
                                    isInputRequired: false,
                                    type: 'text',
                                    value: values.customFieldSelectVal,
                                    disabled: !values.customFieldSelect,
                                }}
                                actions={{
                                    handleFieldChange: value => handleChange('customFieldSelectVal', value, setValue),
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isCustomOptionInput}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id="ticketList.label.customFieldValue" />,
                                    labelClass: [''],
                                }}
                                inputData={{
                                    id: 'customFieldValue',
                                    inputClasses: [''],
                                    options: [
                                        ...defaultOptions,
                                        ...(customFieldOptions?.map(option => ({
                                            value: option.id,
                                            text: option.value,
                                        })) || []),
                                    ],
                                    type: 'dropdown',
                                    value: values.customFieldSelectVal,
                                    isInputRequired: false,
                                }}
                                inputActions={{
                                    handleFieldChange: value => {
                                        handleChange('customFieldSelectVal', value, setValue);
                                    },
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isCustomDateInput}>
                            <DatePicker
                                labelText="ticketList.label.customFieldValue"
                                wrapperClasses={['date-picker']}
                                id="customFieldValue"
                                type="text"
                                pattern={DATE_FORMATS.MM_DD_YYYY}
                                value={values.customFieldSelectVal}
                                maxDate={new Date()}
                                withMask
                                onDateChange={value => handleChange('customFieldSelectVal', value, setValue || '')}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering
                            isTrue={
                                !CustomFiedType.includes(
                                    getCustomField(values.customFieldSelect as customFieldTypes)?.cfieldType,
                                )
                            }
                        >
                            <Input
                                label={{
                                    labelText: 'ticketList.label.customFieldValue',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'customFieldValue',
                                    isInputRequired: false,
                                    type: 'text',
                                    value: values.customFieldSelectVal,
                                    disabled: true,
                                }}
                                actions={{
                                    handleFieldChange: value => handleChange('customFieldSelectVal', value, setValue),
                                }}
                            />
                        </ConditionalRendering>
                    </FlexItem>
                </FlexRow>

                <FlexRow>
                    <FlexItem highlight={highlightedFields?.ticketCategorySelect}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.ticketCategory" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'ticketCategory',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(tkmWorkflows?.map(workflow => ({ value: workflow, text: workflow })) || []),
                                ],
                                type: 'dropdown',
                                value: values.ticketCategorySelect,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => {
                                    if (!value) {
                                        setValue('ticketType', []);
                                    }
                                    handleTicketCategoryChange(value);
                                },
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketType}>
                        <Multiselect
                            id="ticketType"
                            label="ticketList.label.ticketType"
                            value={values.ticketType}
                            options={ticketCategory?.map(category => ({ key: category.type, value: category.type }))}
                            onChange={value => handleChange('ticketType', value, setValue)}
                            isDisabled={Boolean(!values.ticketCategorySelect.length)}
                        />
                    </FlexItem>
                    <StyledControlButton>
                        <GIButton
                            id="advanceResetButton"
                            labelId="ticketList.btn.resetToDefault"
                            type="reset"
                            variant="secondary"
                            isDisabled={false}
                            onClickHandler={onCancel}
                        />
                    </StyledControlButton>

                    <StyledControlButton>
                        <GIButton
                            id="advanceSearchButton"
                            labelId="ticketList.btn.searchAllTicket"
                            type="submit"
                            isDisabled={false}
                        />
                    </StyledControlButton>
                </FlexRow>
            </FlexContainer>
        </StyledForm>
    );
};
